<template>
  <form
    ref="register"
    @keyup.enter="submit()"
    v-on:submit.prevent=""
  >
    <h1>Verify account</h1>

    <div class="row row--separator row--separatorTop">
      <div class="row__full">
        <p v-if="errorMessage" class="row__error" v-html="errorMessage" />
        <p v-else>
          Please type in the verification code we sent you to:<br />
          <span class="highlight--search">{{ registrationEmail }}</span>
        </p>
      </div>
    </div>

    <div class="row row--separator">
      <div class="row__full">
        <FormInput
          :value="verificationCode"
          ref-name="verificationCode"
          placeholder="- - - -"
          label="Verification code"
          alt-label="Fill in verification code from email"
          type="text"
          class="form__input"
          autocomplete="email"
          iconSize="md"
          :icon="icon.verificationCode"
          :error="errors.verificationCode"
          :status="status.verificationCode"
          @inputChanged="setFieldValue($event, 'verificationCode')"
          @unfocused="validate('verificationCode')"
        />
      </div>
    </div>

    <div class="row row--separator">
      <div class="row__full">
        <FormSubmit
          :status="formStatus"
          icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M10 17c0 .552-.447 1-1 1s-1-.448-1-1 .447-1 1-1 1 .448 1 1zm3 0c0 .552-.447 1-1 1s-1-.448-1-1 .447-1 1-1 1 .448 1 1zm3 0c0 .552-.447 1-1 1s-1-.448-1-1 .447-1 1-1 1 .448 1 1zm2-7v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10-4c0-2.206 1.795-4 4-4s4 1.794 4 4v4h-8v-4zm11 16h-14v-10h14v10z"/></svg>'
          @click="submit()"
        >
          {{ formStatus === 'success' ? 'Success' : 'Verify' }}
        </FormSubmit>
      </div>
    </div>

    <div class="row row--separator">
      <div class="row__full">
        <p
          v-if="verificationCodeSent === false"
          class="row__notice"
        >
          If you didn't receive verification code <span class="link" @click="resendVerification()">click here</span> to resend it.
        </p>
        <p
          v-else
          class="row__notice"
        >
          We have sent you a new verification code. If you still did not receive email from us please try again with another email address.
        </p>
      </div>
    </div>
  </form>
</template>

<script>
import FormInput from '@/components/form/Input.vue'
import FormSubmit from '@/components/form/Submit.vue'

export default {
  components: {
    FormInput,
    FormSubmit
  },
  data () {
    return {
      form: false,
      formStatus: 'default',
      verificationCodeSent: false,
      verificationCode: '',
      errorMessage: '',
      errors: {
        verificationCode: false
      },
      status: {
        verificationCode: false
      },
      icon: {
        verificationCode: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 0c-3.371 2.866-5.484 3-9 3v11.535c0 4.603 3.203 5.804 9 9.465 5.797-3.661 9-4.862 9-9.465v-11.535c-3.516 0-5.629-.134-9-3zm-.548 15l-3.452-3.362 1.237-1.239 2.215 2.123 4.382-4.475 1.238 1.238-5.62 5.715z"/></svg>',
      }
    }
  },
  computed: {
    verificationHash () {
      return this.$store.getters['app/getVerificationHash']
    },
    registrationEmail () {
      return this.$store.getters['app/getRegistrationEmail'] === false ? '??' : this.$store.getters['app/getRegistrationEmail']
    }
  },
  methods: {
    async submit () {
      this.errorMessage = ''

      if (this.validate('all')) {
        this.formStatus = 'loading'

        let formData = {
          verification_code: this.verificationCode,
          step: 'c',
          verification_hash: this.verificationHash
        }

        try {
          let response = await this.$store.dispatch('user/register', formData)

          if (response.data.status === 'ok') {
            this.formStatus = 'success'
            this.$emit('verified')
          }
        } catch (error) {
          if (error.data) {
            if (error.data.errors) {
              this.errors = error.data.errors
              error.data.errors.forEach(err => {
                this.errorMessage += err.title + '<br>'
              })
            }
          }
          this.formStatus = 'error'
        }
      }
    },
    async resendVerification () {
      this.errorMessage = ''

      let formData = {
        verification_hash: this.verificationHash
      }

      try {
        let response = await this.$store.dispatch('user/resendVerification', formData)

        if (response.data.status === 'ok') {
          this.verificationCodeSent = true
        }
      } catch (error) {
        if (error.data) {
          if (error.data.errors) {
            this.errors = error.data.errors
            error.data.errors.forEach(err => {
              this.errorMessage += err.title + '<br>'
            })
          }
        }
      }
    },
    setFieldValue (value, field) {
      this[field] = value
    },
    validate (field) {
      let valid = true

      if (field === 'verificationCode' || field === 'all') {
        if (this.verificationCode.length !== 4) {
          this.errors.verificationCode = 'Code must be 4 digits long'
          valid = false
        } else {
          this.errors.verificationCode = undefined
        }

        this.status.verificationCode = this.errors.verificationCode ? 'invalid' : 'valid'
      }

      return valid
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

h1, h2 {
  @include font('header--xl', $white, 0.9);
  width: 90%;
  margin: 0 auto;
  text-align: center;
  text-transform: uppercase;

  @media screen and (min-width: $breakpointLg) {
    @include font('header--xxl', $white, 0.9);
    margin: 0 auto;
  }
}

p {
  @include font('content--md', $white);

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white);
  }
}

.row {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0.75rem auto;
  overflow: hidden;

  @media screen and (min-width: $breakpointLg) {
    margin: 1rem auto;
  }

  &--separator {
    border-top: 1px solid rgba($white, 0.07);
    padding-top: calc(1rem - 1px);
    margin-top: 1rem;

    &Top {
      margin-top: 0.5rem;
    }
  }

  &__full {
    flex: 1;
    text-align: center;
  }

  &__left {
    flex: 0.2;
    text-align: left;
  }

  &__right {
    flex: 0.8;
    text-align: left;
  }

  &__notice {
    color: rgba($white, 0.6);
    margin: 0;
  }

  &__error {
    color: $red;
    margin: 0;
  }

  &__label {
    color: rgba($white, 0.9);
    text-align: left;
  }
}

</style>
